import { useEffect } from 'react';

import {
  DATAWRAPPER_IFRAME_DOMAIN,
  IF_CDN_IFRAME_DOMAIN,
  TI_IFRAME_DOMAIN,
  YOUTUBE_IFRAME_DOMAIN,
} from '@utils/constants';

import debounce from '../application/javascripts/debounce';
import useServerSideRendering from './useServerSideRendering';

const IFRAME_RESIZE = 'iframe.resize';

const resizeIframe = (event: MessageEvent) => {
  if (
    (event.origin !== TI_IFRAME_DOMAIN && event.origin !== DATAWRAPPER_IFRAME_DOMAIN) ||
    event.data?.context !== IFRAME_RESIZE
  )
    return;
  const targetIframe = document.querySelector(`[name="${event.data?.name}"]`) as HTMLIFrameElement;
  if (targetIframe) {
    targetIframe.height = event.data?.height;
    targetIframe.width = event.data?.width;
  }
};

const useIframeResizeMessager = () => {
  const hydrated = useServerSideRendering();
  useEffect(() => {
    if (!hydrated) return;

    const targetIframes = document.querySelectorAll(
      `iframe[src*="${TI_IFRAME_DOMAIN}"],
       iframe[src*="${DATAWRAPPER_IFRAME_DOMAIN}"],
       iframe[src*="${IF_CDN_IFRAME_DOMAIN}"],
       iframe[src*="${YOUTUBE_IFRAME_DOMAIN}"]`
    );

    function transmitIframeResizeMessage() {
      targetIframes?.forEach((target: HTMLIFrameElement) => {
        if (
          target.src.includes(IF_CDN_IFRAME_DOMAIN) ||
          target.src.includes(YOUTUBE_IFRAME_DOMAIN)
        ) {
          target.style.aspectRatio = '16/9';
          target.style.height = 'auto';
          target.style.border = 'none';
        } else {
          target.contentWindow.postMessage(
            {
              context: IFRAME_RESIZE,
            },
            TI_IFRAME_DOMAIN
          );
        }
      });
    }

    function resizeIframeAndTransmitMessage() {
      targetIframes?.forEach((target: HTMLIFrameElement) => {
        target.width = target.parentElement.getBoundingClientRect().width.toString();
      });
      window.setTimeout(transmitIframeResizeMessage, 10);
    }

    const debouncedResizeIframe = debounce(resizeIframeAndTransmitMessage, 200);

    window.addEventListener('message', resizeIframe, true);
    window.addEventListener('resize', debouncedResizeIframe, true);

    transmitIframeResizeMessage();

    return () => {
      window.removeEventListener('message', resizeIframe, true);
      window.removeEventListener('resize', debouncedResizeIframe, true);
    };
  }, [hydrated]);
};

export default useIframeResizeMessager;

/**
 * Below is code used inside the iframes to communicate back their getBoundingClientRect
 * width and height. The Graphics team adds this to their Illustrator files and
 * it gets extracted via ai2html. See this ticket for more information:
 * {@link https://app.asana.com/0/1207359392432611/1207436000889604/f}
 * Placing here for lack of a better location.
 */
/*
<script type="text/javascript">
  (() => {
    "use strict";

    const ALLOWED_ORIGINS = [
      "http://localhost:3000",
      "https://info-reader-sandbox.herokuapp.com",
      "https://info-reader-staging.herokuapp.com",
      "https://theinformation.com",
      "https://www.theinformation.com",
      "https://reader-jm-feat-iframe-r-univcx.herokuapp.com",
    ];

    const _container = document.querySelector(".ai2html");
    const parentWindow = window.parent;

    function setIframeDimensions() {
      const boundingRect = _container.getBoundingClientRect();
      const width = Math.floor(boundingRect.width);
      const height = Math.floor(boundingRect.height);
      parentWindow.postMessage(
        {
          context: "iframe.resize",
          name: window.name,
          height,
          width,
        },
        "*"
      );
    }

    function captureMessage(event) {
      if (!ALLOWED_ORIGINS.includes(event.origin) || event.data?.context !== "iframe.resize")
        return;
      setIframeDimensions();
    }

    window.addEventListener("message", captureMessage, true);
  })();
</script>
*/
